import React, { useState, forwardRef, useEffect, useCallback } from 'react';
import DatePicker from 'react-datepicker';
import { Box, Flex, Text, useDisclosure, Portal } from '@chakra-ui/react';
import 'react-datepicker/dist/react-datepicker.css';
import './AleaDatePicker.scss';
import AleaCalendarIcon from '../../icons/AleaCalendarIcon';
import AleaCheckIcon from '../../icons/AleaCheckIcon';
import AleaCloseIcon from '../../icons/AleaCloseIcon';
import AleaChevronRightIcon from '../../icons/AleaChevronRightIcon';
import { useTranslation } from 'react-i18next';
import { registerLocale } from 'react-datepicker';
import { de } from 'date-fns/locale';
import { enGB } from 'date-fns/locale';
import useHeaderZIndex from '../../hooks/useHeaderZIndex';

// Register locales that we'll use
registerLocale('de', de);
registerLocale('en', enGB);

interface AleaDatePickerProps {
  selected: Date | null;
  onChange: (date: Date | null) => void;
  placeholderText?: string;
  dateFormat?: string;
  onCancel?: () => void;
  onCalendarOpen?: () => void;
  onCalendarClose?: () => void;
  minDate?: Date;
  maxDate?: Date;
}

// Custom input component
const CustomInput = forwardRef<HTMLDivElement, any>(
  ({ value, onClick, placeholder, showIcon = true }, ref) => (
    <Flex
      ref={ref}
      onClick={onClick}
      align="center"
      borderRadius="100px"
      height="50px"
      bg="white"
      border="1px solid #DCDCDC"
      px="15px"
      cursor="pointer"
      justifyContent="space-between"
    >
      <Text fontSize="18px">{value || placeholder || 'DD.MM.YYYY'}</Text>
      {showIcon && <AleaCalendarIcon size="19" color="#2B4956" />}
    </Flex>
  )
);

CustomInput.displayName = 'CustomInput';

const AleaDatePicker: React.FC<AleaDatePickerProps> = ({
  selected,
  onChange,
  placeholderText = 'TT.MM.JJJJ',
  dateFormat = 'dd.MM.yyyy',
  onCancel,
  onCalendarOpen,
  onCalendarClose,
  minDate,
  maxDate,
}) => {
  const [selectedDate, setSelectedDate] = useState<Date | null>(selected);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { i18n } = useTranslation();
  const { lowerHeaderZIndex, resetHeaderZIndex } = useHeaderZIndex();
  
  // Set locale based on current language
  const currentLocale = i18n.language === 'de' ? 'de' : 'en';

  // Reference to control modal opening
  const headerFixedRef = React.useRef(false);
  
  // Fix z-index issues
  const fixHeaderZIndexIssue = useCallback(() => {
    if (headerFixedRef.current) return;
    
    try {
      // Select Chakra modal components
      const modalContent = document.querySelector('.chakra-modal__content');
      const modalOverlay = document.querySelector('.chakra-modal__overlay');
      const modalHeader = document.querySelector('.react-datepicker__header');
      const customHeader = document.querySelector('.custom-header');
      const datepickerBackdrop = document.querySelector('.datepicker-backdrop');
      
      // Set z-index values
      if (modalContent) {
        modalContent.setAttribute('style', `${modalContent.getAttribute('style') || ''}; z-index: 9999 !important;`);
      }
      
      if (modalOverlay) {
        modalOverlay.setAttribute('style', `${modalOverlay.getAttribute('style') || ''}; z-index: 9998 !important;`);
      }
      
      if (modalHeader) {
        modalHeader.setAttribute('style', 'position: relative !important; z-index: 10 !important;');
      }
      
      if (customHeader) {
        customHeader.setAttribute('style', 'position: relative !important; z-index: 10 !important;');
      }
      
      if (datepickerBackdrop) {
        datepickerBackdrop.setAttribute('style', `${datepickerBackdrop.getAttribute('style') || ''}; z-index: 9999 !important;`);
      }
      
      // Lower the header's z-index (now using the hook)
      lowerHeaderZIndex();
      
      headerFixedRef.current = true;
    } catch (error) {
      console.error('Error fixing header z-index:', error);
    }
  }, [lowerHeaderZIndex]);

  // Notify parent when calendar opens or closes
  useEffect(() => {
    if (isOpen) {
      if (onCalendarOpen) onCalendarOpen();
      
      // Try fixing multiple times in sequence
      setTimeout(fixHeaderZIndexIssue, 10);
      setTimeout(fixHeaderZIndexIssue, 50);
      setTimeout(fixHeaderZIndexIssue, 100);
    } else {
      if (onCalendarClose) onCalendarClose();
      resetHeaderZIndex();
      headerFixedRef.current = false;
    }
  }, [isOpen, onCalendarOpen, onCalendarClose, fixHeaderZIndexIssue, resetHeaderZIndex]);

  // Ensure empty rows are filled to maintain height
  const handleMonthChange = useCallback(() => {
    setTimeout(() => {
      const calendarBody = document.querySelector('.react-datepicker__month');
      if (calendarBody) {
        const weeks = calendarBody.querySelectorAll('.react-datepicker__week');
        if (weeks.length < 6) {
          // If there are fewer than 6 rows, add extra space for empty rows
          calendarBody.classList.add('five-rows');
        } else {
          calendarBody.classList.remove('five-rows');
        }
      }
    }, 10);
  }, []);

  // Handle date change
  const handleDateChange = (date: Date | null) => {
    setSelectedDate(date);
    // Add styling after selection using DOM manipulation
    setTimeout(() => {
      // First clear styles from all days
      const allDays = document.querySelectorAll('.react-datepicker__day');
      allDays.forEach(el => {
        if (el instanceof HTMLElement) {
          el.style.backgroundColor = '';
          el.style.color = '';
          el.style.fontWeight = '';
          el.style.boxShadow = '';
          el.style.transform = '';
        }
      });
      
      // Set style only for the selected day
      const selectedDays = document.querySelectorAll('.react-datepicker__day--selected');
      selectedDays.forEach(el => {
        if (el instanceof HTMLElement) {
          el.style.backgroundColor = '#2B4956';
          el.style.color = 'white';
          el.style.fontWeight = '500';
          el.style.boxShadow = '0 2px 5px rgba(43, 73, 86, 0.3)';
          el.style.transform = 'scale(1.05)';
        }
      });
    }, 10);
  };

  // This effect is for applying style to the correct day when modal opens
  useEffect(() => {
    if (isOpen && selectedDate) {
      setTimeout(() => {
        // First clear styles from all days
        const allDays = document.querySelectorAll('.react-datepicker__day');
        allDays.forEach(el => {
          if (el instanceof HTMLElement) {
            el.style.backgroundColor = '';
            el.style.color = '';
            el.style.fontWeight = '';
            el.style.boxShadow = '';
            el.style.transform = '';
          }
        });
        
        // Set style only for the selected day
        const selectedDays = document.querySelectorAll('.react-datepicker__day--selected');
        selectedDays.forEach(el => {
          if (el instanceof HTMLElement) {
            el.style.backgroundColor = '#2B4956';
            el.style.color = 'white';
            el.style.fontWeight = '500';
            el.style.boxShadow = '0 2px 5px rgba(43, 73, 86, 0.3)';
            el.style.transform = 'scale(1.05)';
          }
        });
      }, 10);
    }
  }, [isOpen, selectedDate]);

  // Handle apply button click
  const handleApply = () => {
    onChange(selectedDate);
    onClose();
  };

  // Handle cancel button click
  const handleCancel = () => {
    setSelectedDate(selected); // Reset to original value
    onClose();
    if (onCancel) onCancel();
  };

  // Custom header to match the design
  const CustomHeader = ({
    date,
    decreaseMonth,
    increaseMonth,
    prevMonthButtonDisabled,
    nextMonthButtonDisabled,
  }: any) => {
    // Use the current locale for month formatting
    const month = date.toLocaleString(i18n.language === 'de' ? 'de-DE' : 'en-GB', { month: 'long' });
    const year = date.getFullYear();

    return (
      <Flex 
        className="custom-header" 
        width="100%" 
        justifyContent="space-between" 
        alignItems="center" 
        paddingTop="0" 
        borderBottom="1px solid #DCDCDC"
        pb="10px"
        mb="10px"
        position="relative"
        zIndex="3"
      >
        <Box display="flex" alignItems="center" justifyContent="flex-start">
          <Box
            as="button"
            onClick={decreaseMonth}
            disabled={prevMonthButtonDisabled}
            className="nav-button"
            display="flex"
            alignItems="center"
            justifyContent="center"
            height="36px"
            width="36px"
            borderRadius="8px"
            transition="all 0.2s ease"
            opacity={prevMonthButtonDisabled ? 0.5 : 1}
          >
            <Box 
              transform="rotate(180deg)"
              display="flex" 
              alignItems="center"
            >
              <AleaChevronRightIcon 
                color={prevMonthButtonDisabled ? "#ccc" : "#2B4956"}
                size="36"
              />
            </Box>
          </Box>
        </Box>
        <Text fontSize="18px" fontWeight="500" className="month-year-text" display="flex" alignItems="center">
          {month} {year}
        </Text>
        <Box display="flex" alignItems="center" justifyContent="flex-end">
          <Box
            as="button"
            onClick={increaseMonth}
            disabled={nextMonthButtonDisabled}
            className="nav-button"
            display="flex"
            alignItems="center"
            justifyContent="center"
            height="36px"
            width="36px"
            borderRadius="8px"
            transition="all 0.2s ease"
            opacity={nextMonthButtonDisabled ? 0.5 : 1}
          >
            <Box 
              display="flex" 
              alignItems="center"
            >
              <AleaChevronRightIcon 
                color={nextMonthButtonDisabled ? "#ccc" : "#2B4956"}
                size="36"
              />
            </Box>
          </Box>
        </Box>
      </Flex>
    );
  };

  return (
    <Box className="alea-date-picker" position="relative">
      {/* Custom Input */}
      <CustomInput 
        value={selected ? selected.toLocaleDateString(i18n.language === 'de' ? 'de-DE' : 'en-GB').split('.').join('.') : ''} 
        onClick={onOpen} 
        placeholder={placeholderText}
      />
      
      {/* Calendar Modal */}
      {isOpen && (
        <Portal>
          <Box
            position="fixed"
            left="0"
            top="0"
            width="100%"
            height="100%"
            bg="rgba(0, 0, 0, 0.4)"
            zIndex="9999"
            display="flex"
            alignItems="center"
            justifyContent="center"
            onClick={onClose}
            className="datepicker-backdrop"
          >
            <Box
              bg="white"
              borderRadius="10px"
              maxW="350px"
              width="100%"
              p={4}
              fontFamily="inherit"
              onClick={(e) => e.stopPropagation()}
              zIndex="10000"
              position="relative"
            >
              <Box className="calendar-wrapper" position="relative" zIndex="2" width="100%">
                <Box className="calendar-container" fontFamily="inherit" fontSize="inherit" position="relative" zIndex="2" width="100%">
                  <DatePicker
                    selected={selectedDate}
                    onChange={handleDateChange}
                    inline
                    dateFormat={dateFormat}
                    renderCustomHeader={CustomHeader}
                    calendarClassName="alea-calendar modal-calendar"
                    locale={currentLocale}
                    onMonthChange={handleMonthChange}
                    fixedHeight
                    minDate={minDate}
                    maxDate={maxDate}
                  />
                </Box>
              
                {/* Action Buttons */}
                <Flex justifyContent="space-between" mt={4}>
                  <Box 
                    as="button"
                    onClick={handleCancel}
                    border="1px solid #DCDCDC"
                    borderRadius="10px"
                    width="48%"
                    height="40px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    bg="white"
                    transition="all 0.2s"
                    _hover={{
                      bg: "#f5f5f5"
                    }}
                  >
                    <AleaCloseIcon color="#2B4956" size="19" />
                  </Box>
                  <Box 
                    as="button"
                    onClick={handleApply}
                    borderRadius="10px"
                    width="48%"
                    height="40px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    bg="#2B4956"
                    color="white"
                    transition="all 0.2s"
                    _hover={{
                      bg: "#1e3340"
                    }}
                  >
                    <AleaCheckIcon color="white" size="19" />
                  </Box>
                </Flex>
              </Box>
            </Box>
          </Box>
        </Portal>
      )}
    </Box>
  );
};

export default AleaDatePicker;